import { useEffect, useState, type ReactNode } from "react";
import type { SnapshotMeta, Country, SyncStatus } from "@shared/types.js";
import { COUNTRIES } from "@shared/types.js";
import { fstamp } from "../lib/format.js";
import { type Theme, NEXT_THEME, THEME_LABEL, THEME_ICON } from "../lib/theme.js";

export type TabKey =
  | "overview" | "worklist" | "newrate" | "estimation" | "components"
  | "data" | "crosscheck" | "trend" | "pic" | "export" | "mapping";

interface Tab {
  key: TabKey;
  label: string;
  icon: string;
}

/**
 * The nav groups, in the order the work actually flows.
 *
 * Overview has no group of its own: a dropdown holding one item is a worse
 * affordance than a plain link, so it is rendered as one.
 */
export const NAV_GROUPS: { name: string; tabs: Tab[] }[] = [
  {
    name: "Overview",
    tabs: [
      { key: "overview", label: "Overview", icon: "bi-speedometer2" },
    ],
  },
  {
    name: "Rates",
    tabs: [
      { key: "worklist", label: "Worklist", icon: "bi-list-check" },
      { key: "newrate", label: "New Rate", icon: "bi-tag" },
      { key: "estimation", label: "Estimation", icon: "bi-calculator" },
      { key: "components", label: "Components", icon: "bi-boxes" },
    ],
  },
  {
    name: "Data",
    tabs: [
      { key: "data", label: "Detailed Data", icon: "bi-table" },
      { key: "crosscheck", label: "Crosscheck", icon: "bi-shield-check" },
    ],
  },
  {
    name: "Team",
    tabs: [
      { key: "trend", label: "Progress", icon: "bi-graph-up-arrow" },
      { key: "pic", label: "Workload", icon: "bi-people" },
    ],
  },
  {
    name: "Reference",
    tabs: [
      { key: "export", label: "Export", icon: "bi-download" },
      { key: "mapping", label: "Field mapping", icon: "bi-diagram-3" },
    ],
  },
];

export const TABS: Tab[] = NAV_GROUPS.flatMap((g) => g.tabs);

/** Which group a tab belongs to, for the breadcrumb and the active highlight. */
function groupOf(tab: TabKey): { name: string; tab: Tab } | undefined {
  for (const g of NAV_GROUPS) {
    const t = g.tabs.find((x) => x.key === tab);
    if (t) return { name: g.name, tab: t };
  }
  return undefined;
}

/* ============================ top navigation ============================= */

/**
 * One dropdown group. Desktop opens it on hover (CSS); a click opens it too,
 * for touch and for keyboards. `shut` is what stops a hovered menu springing
 * back open the moment an item inside it is chosen - the pointer is still
 * over the group at that point, so hover alone would reopen it.
 */
function NavGroup({
  name, tabs, tab, counts, onPick,
}: {
  name: string;
  tabs: Tab[];
  tab: TabKey;
  counts: Partial<Record<TabKey, number>>;
  onPick: (t: TabKey) => void;
}) {
  const [open, setOpen] = useState(false);
  const [shut, setShut] = useState(false);
  const holdsActive = tabs.some((t) => t.key === tab);

  return (
    <div
      className={`jx-dd${open ? " open" : ""}${shut ? " jx-shut" : ""}`}
      data-group={name}
      onMouseLeave={() => { setOpen(false); setShut(false); }}
    >
      <button
        type="button" className="jx-dd-btn"
        aria-expanded={open} aria-haspopup="true"
        onClick={() => { setOpen((v) => !v); setShut(false); }}
      >
        {name}
        <svg className="jx-chev" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
      </button>
      <div className="jx-dd-menu" role="menu">
        {tabs.map((t) => {
          const n = counts[t.key];
          return (
            <button
              key={t.key} type="button" role="menuitem"
              className={`jx-dd-item${tab === t.key ? " active" : ""}`}
              onClick={() => { onPick(t.key); setOpen(false); setShut(true); }}
            >
              <i className={`bi ${t.icon}`} aria-hidden="true" />
              {t.label}
              {n !== undefined && <span className="jx-dd-n">{n.toLocaleString("en-US")}</span>}
            </button>
          );
        })}
      </div>
      {/* The group itself is highlighted when it holds the open page. CSS does
          this with :has(.active); this keeps it working where :has does not. */}
      {holdsActive && <span hidden aria-hidden="true" className="active" />}
    </div>
  );
}

export function TopNav({
  tab, onTab, counts, search, onSearch, theme, onCycleTheme,
  sync, syncing, onSync, onOpenUpload, onManage, onSignOut,
}: {
  tab: TabKey;
  onTab: (t: TabKey) => void;
  counts: Partial<Record<TabKey, number>>;
  search: string;
  onSearch: (v: string) => void;
  theme: Theme;
  onCycleTheme: () => void;
  sync: SyncStatus | null;
  syncing: boolean;
  onSync: () => void;
  onOpenUpload: () => void;
  onManage: () => void;
  onSignOut: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  /* The nav gains a shadow once the page is off the top, so it separates from
     the card sliding under it. */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const pick = (t: TabKey) => { onTab(t); setMenuOpen(false); };

  return (
    <header className={`jx-nav${scrolled ? " scrolled" : ""}${menuOpen ? " jx-open" : ""}`}>
      <div className="jx-nav-in">
        <button
          className="jx-burger" type="button"
          aria-label={menuOpen ? "Close menu" : "Menu"} aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
        >
          <i className={`bi ${menuOpen ? "bi-x-lg" : "bi-list"}`} aria-hidden="true" />
        </button>

        <span className="jx-logo">
          <img src="/brand/happy-trails-logo.png" alt="Happy Trails Asia" width={440} height={184} />
          <span className="jx-logo-copy">
            <strong>Data Entry Masterfile</strong>
            <span>Tracking Dashboard</span>
          </span>
        </span>

        <nav className="jx-menu" aria-label="Main navigation">
          {NAV_GROUPS.map((g) =>
            g.tabs.length === 1 && g.tabs[0] ? (
              <button
                key={g.name} type="button"
                className={`jx-link${tab === g.tabs[0].key ? " active" : ""}`}
                onClick={() => pick(g.tabs[0]!.key)}
              >
                {g.name}
              </button>
            ) : (
              <NavGroup
                key={g.name} name={g.name} tabs={g.tabs}
                tab={tab} counts={counts} onPick={pick}
              />
            ),
          )}
        </nav>

        <input
          className="jx-search" type="search" value={search}
          placeholder="Search supplier, code, town…" aria-label="Search records"
          onChange={(e) => onSearch(e.target.value)}
        />

        <div className="jx-right">
          <div className="jx-dd" data-group="Data">
            <button type="button" className="jx-dd-btn">
              Data
              <svg className="jx-chev" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
            </button>
            <div className="jx-dd-menu jx-dd-end" role="menu">
              {/* Only offered when the server actually holds credentials: a
                  button that can only fail is worse than no button. */}
              {sync?.configured && (
                <button
                  className="jx-dd-item" type="button" role="menuitem"
                  onClick={onSync} disabled={syncing} title={syncTitle(sync)}
                >
                  <i className={`bi bi-arrow-repeat${syncing ? " spin" : ""}`} aria-hidden="true" />
                  {syncing ? "Syncing…" : "Sync now"}
                </button>
              )}
              <button className="jx-dd-item" type="button" role="menuitem" onClick={onOpenUpload}>
                <i className="bi bi-upload" aria-hidden="true" />Upload Excel
              </button>
              <button className="jx-dd-item" type="button" role="menuitem" onClick={onManage}>
                <i className="bi bi-archive" aria-hidden="true" />Manage snapshots
              </button>
            </div>
          </div>

          <button
            className="jx-icon" type="button" onClick={onCycleTheme}
            title={`${THEME_LABEL[theme]} — click for ${THEME_LABEL[NEXT_THEME[theme]].toLowerCase()}`}
            aria-label={THEME_LABEL[theme]}
          >
            <i className={THEME_ICON[theme]} aria-hidden="true" />
          </button>

          <button className="jx-lock" type="button" onClick={onSignOut} title="Sign out" aria-label="Sign out">
            <i className="bi bi-box-arrow-right" aria-hidden="true" />
          </button>
        </div>
      </div>
    </header>
  );
}

/* ================================= banner ================================ */

/**
 * The title banner. It carries what the sidebar footer and the page heading
 * used to carry between them: where you are, which Masterfile is open, and
 * which snapshot the numbers come from.
 */
export function Hero({
  tab, country, onCountry, snapshots, currentId, onSelectSnapshot, recordCount, snapshot,
}: {
  tab: TabKey;
  country: Country;
  onCountry: (c: Country) => void;
  snapshots: SnapshotMeta[];
  currentId: number | null;
  onSelectSnapshot: (id: number) => void;
  recordCount: number | null;
  snapshot: SnapshotMeta | undefined;
}) {
  const here = groupOf(tab);
  const title = here?.tab.label ?? "Overview";
  /* "Home - Group - Page", with the group left out when it is the page. */
  const crumb = here && here.name !== title ? `${here.name} - ${title}` : title;

  return (
    <section className="jx-hero">
      <div className="jx-hero-in">
        <div>
          <div className="jx-title">{title}</div>
          <div className="jx-crumb">Home - {crumb}</div>
          <div className="jx-meta">
            <b>Rate 27&ndash;28</b>
            {recordCount !== null && <span>· {recordCount.toLocaleString("en-US")} records in scope</span>}
            {snapshot && <span className="jx-badge">{snapshot.generatedAt.slice(0, 10)}</span>}
          </div>
        </div>

        <div className="jx-hero-side">
          <div className="jx-seg" role="group" aria-label="Country">
            {COUNTRIES.map((c) => (
              <button
                key={c.key} type="button"
                className={country === c.key ? "on" : undefined}
                aria-pressed={country === c.key} title={c.label}
                onClick={() => onCountry(c.key)}
              >
                {c.key} <span className="d-none d-sm-inline">{c.label}</span>
              </button>
            ))}
          </div>

          {snapshots.length > 0 && (
            <select
              className="jx-snap" value={currentId ?? ""} aria-label="Snapshot"
              onChange={(e) => onSelectSnapshot(Number(e.target.value))}
              title={snapshot ? `${snapshot.sourceFile} — ${fstamp(snapshot.generatedAt)}` : undefined}
            >
              {snapshots.map((s) => (
                <option key={s.id} value={s.id}>
                  {(s.label ?? s.sourceFile)} — {s.generatedAt.slice(0, 10)}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>
    </section>
  );
}

/** What the Sync item says on hover: the last outcome, not just the label. */
function syncTitle(s: SyncStatus): string {
  if (s.error) return `Last sync failed: ${s.error}`;
  const parts = [`Pull ${s.fileName ?? "the Masterfile"} from SharePoint now`];
  if (s.lastSyncedAt) parts.push(`last change picked up ${fstamp(s.lastSyncedAt)}`);
  else if (s.lastCheckedAt) parts.push(`last checked ${fstamp(s.lastCheckedAt)}, no change yet`);
  if (s.pollSeconds) parts.push(`auto-checks every ${Math.round(s.pollSeconds / 60)} min`);
  return parts.join(" · ");
}

/** Theme-styled page title block, shown at the top of every panel. */
export function PageHeading({
  icon, eyebrow, title, subtitle, actions,
}: {
  icon: string; eyebrow: string; title: string;
  subtitle: ReactNode; actions?: ReactNode;
}) {
  return (
    <div className="page-heading">
      <div className="page-heading-copy">
        <span className="page-icon"><i className={`bi ${icon}`} aria-hidden="true" /></span>
        <div>
          <p className="eyebrow mb-1">{eyebrow}</p>
          <h1 className="h4 mb-1">{title}</h1>
          <p className="text-muted mb-0">{subtitle}</p>
        </div>
      </div>
      {actions && <div className="heading-actions">{actions}</div>}
    </div>
  );
}
